import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Eye, Pencil, Plus, FileBox, ChevronRight, ChevronLeft, Download } from 'lucide-react'
import { PageContainer } from '@/component/layout'

import { useModality } from '@/hooks/use-modality'
import { useClusterMapping } from '@/hooks/use-cluster-mappings'
import { Toggle } from '@/component/ui/toogle'
import { Button } from '@/component/ui/button'
import { Spinner } from '@/component/ui/spinner'
import { toBool } from '@/utils/booleans'
import { PromptModal } from '@/component/modality/prompt-modal'
import { SuccessModal } from '@/component/modality/success-modal'
import { patchModalityStatus } from '@/services/modality'

const ITEMS_PER_PAGE = 10

export const Modality = () => {
  const navigate = useNavigate()

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)

  // Calculate offset for API call
  const offset = (currentPage - 1) * ITEMS_PER_PAGE

  // Update hook to accept pagination params
  const { data, loading, error, totalCount } = useModality(ITEMS_PER_PAGE, offset)
  const [rows, setRows] = useState<any[]>([])

  useEffect(() => {
    setRows(Array.isArray(data) ? data : [])
    if (totalCount) {
      setTotalPages(Math.ceil(totalCount / ITEMS_PER_PAGE))
    }
  }, [data, totalCount])

  const { sending, success: showSuccess, setSuccess: setShowSuccess, runOneTime } = useClusterMapping(rows)

  const [showPromptModal, setShowPromptModal] = useState(false)
  const openRunModal = () => setShowPromptModal(true)

  // PATCH /data-api/modalities/:id { status }
  const toggleStatus = async (row: any, next: boolean) => {
    // optimistic UI
    setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, status: next, isActive: next, is_active: next } : r)))
    try {
      await patchModalityStatus(row.id, next)
    } catch (e: any) {
      // revert on failure
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, status: !next, isActive: !next, is_active: !next } : r)))
      alert(`Failed to update status: ${e?.message || e}`)
    }
  }

  const closeSuccessAndRefresh = () => {
    setShowSuccess(false)
    navigate(0)
  }

  // Sort Table and logic
  // const [sortField, setSortField] = useState<string | null>(null)
  // const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')
  const [sortField ] = useState<string | null>(null)
  const [sortDirection ] = useState<'asc' | 'desc'>('asc')

  const handleSort = (field: string) => {
    console.log('Sorting for ' + field + ' is disabled.');
    // if (sortField === field) {
    //   setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    // } else {
    //   setSortField(field)
    //   setSortDirection('asc')
    // }
  }

  const sortedRows = [...rows].sort((a, b) => {
    if (!sortField) return 0
    const aVal = a[sortField] ?? ''
    const bVal = b[sortField] ?? ''
    return sortDirection === 'asc' ? String(aVal).localeCompare(String(bVal)) : String(bVal).localeCompare(String(aVal))
  })

  // Pagination handlers

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1)
    }
  }

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1)
    }
  }

  //
  const startIndex = offset + 1
  const endIndex = Math.min(offset + ITEMS_PER_PAGE, totalCount || 0)

  // Updated loading state with spinner
  if (loading) {
    return (
      <PageContainer>
        <div className='flex flex-col items-center justify-center min-h-[400px] text-center'>
          <Spinner />
          <p className='mt-4 text-lg text-gray-600'>Loading modalities...</p>
        </div>
      </PageContainer>
    )
  }

  if (error) return <PageContainer className='text-red-600'>Error: {error}</PageContainer>

  return (
    <PageContainer>
      {/* Header */}
      <div className='mb-6 flex items-center justify-between'>
        <div className='flex items-center gap-2'>
          <div className='w-8 h-8  bg-[#E6F9EF] rounded-full flex items-center justify-center'>
            <FileBox className='w-4 h-4 text-gray-500' />
          </div>
          <h1 className='text-3xl font-bold'>Modality manager</h1>
        </div>
        <div className='flex items-center gap-3'>
          <Button
            variant='violetOutline'
            size='cta'
            onClick={openRunModal}
            disabled={sending}
            aria-label='Generate cluster map'
            className='proxima-nova gap-2 border-3 font-semibold'>
            {sending ? 'Mapping...' : 'Generate cluster map'}
          </Button>

          {/* This button class should be updated marketing standard has been updated*/}
          <Button asChild variant='dark' size='cta' className='proxima-nova gap-2 border-3 border-black font-semibold hover:bg-white hover:text-black'>
            <Link to='/modalities/new' aria-label='Add Modality'>
              <span>Add new modality</span>
              <Plus className='h-5 w-5 gap-2' aria-hidden='true' />
            </Link>
          </Button>
        </div>
      </div>

      {/* Intro */}
      <p className='mb-6 max-w-8xl text-gray-600'>
        A modality is a specific learning activity—like peer tutoring or case studies—that helps students learn by engaging them in a focused, structured
        experience. Below is a list of the identified learning modalities.
      </p>

      <div className='flex items-center gap-2 mb-4'>
        <h3 className='text-base font-semibold'>Learning modality library</h3>
        {/* <Info className='w-4 h-4 text-gray-500' /> */}
      </div>

      <p className='text-sm text-gray-600 mb-6'>
        Showing {startIndex}-{endIndex} out of {totalCount || 0} results
      </p>

      {/* Table */}
      <div className='overflow-x-auto'>
        <table className='min-w-full text-left'>
          <thead className='bg-grey-300 font-proxima-nova'>
            <tr>
              {/* Enable Checkbox when flow is ready */}
              {/* <th className='px-4 py-3'>
                <input type='checkbox' className='h-4 w-4 rounded border-gray-300' />
              </th> */}

              {/* Modality Name */}
              <th className='px-4 py-3 cursor-pointer font-semibold' onClick={() => handleSort('name')}>
                Modality Name {sortField === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>

              {/* Description */}
              {/* <th className='px-4 py-3'>Description</th> */}

              {/* Associated Cluster */}
              <th className='px-4 py-3 cursor-pointer font-semibold' onClick={() => handleSort('associatedCluster')}>
                Associated Cluster {sortField === 'associatedCluster' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>

              {/* Created by */}
              <th className='px-4 py-3 cursor-pointer font-semibold' onClick={() => handleSort('createdBy')}>
                Created by {sortField === 'createdBy' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>

              {/* Date Added */}
              <th className='px-4 py-3 cursor-pointer font-semibold' onClick={() => handleSort('created')}>
                Date Added {sortField === 'created' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>

              <th className='px-4 py-3 text-center font-semibold'>Status</th>
              <th className='px-4 py-3 text-center font-semibold'>Actions</th>
            </tr>
          </thead>
          <tbody className='divide-y divide-gray-200 font-proxima-nova'>
            {sortedRows.length ? (
              sortedRows.map((row: any) => {
                const checked = toBool(row.isActive ?? row.status ?? row.is_active)
                return (
                  <tr key={row.id} className='hover:bg-gray-50'>
                    {/* Enable Checkbox when flow is ready */}
                    {/* <td className='px-4 py-3'>
                      <input type='checkbox' className='h-4 w-4 rounded border-gray-300' />
                    </td> */}
                    <td className='px-4 py-3 font-medium'>
                      <Link to={`/modalities/${row.id}`} className='text-blue-700 outline-none hover:underline focus:underline'>
                        {row.name}
                      </Link>
                    </td>
                    {/* <td className='max-w-md truncate px-4 py-3 text-gray-700'>{row.description}</td> */}
                    <td className='px-4 py-3 text-gray-700' title={row.associatedCluster || ''}>
                      {row.associatedCluster?.trim() || row.primaryClusterName?.trim() || '—'}
                    </td>
                    <td className='px-4 py-3'>{row.createdBy ?? '—'}</td>
                    <td className='px-4 py-3'>
                      {row.created
                        ? new Date(row.created).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                          })
                        : '—'}
                    </td>
                    <td className='px-4 py-3 text-center'>
                      <Toggle
                        checked={checked}
                        onChange={(e) => toggleStatus(row, e.currentTarget.checked)}
                        aria-checked={checked}
                        label={`Toggle status for ${row.name}`}
                        tone='deepBlue'
                        size='md'
                      />
                    </td>
                    <td className='px-4 py-3 text-center'>
                      <div className='mx-auto flex w-max items-center justify-center gap-3 text-gray-500'>
                        <Link to={`/modalities/${row.id}`} title='View' className='inline-flex'>
                          <Eye className='h-5 w-5 hover:text-gray-800' />
                        </Link>
                        <Link to={``} title='View' className='inline-flex cursor-not-allowed opacity-50' onClick={(e) => e.preventDefault()}>
                          <Download className='h-5 w-5 text-gray-400' />
                        </Link>

                        <Link to={`/modalities/${row.id}/edit`} title='Edit' className='inline-flex'>
                          <Pencil className='h-5 w-5 hover:text-gray-800' />
                        </Link>
                        {/* <button title='Delete'>
                          <Trash2 className='h-5 w-5 hover:text-red-600' />
                        </button> */}
                      </div>
                    </td>
                  </tr>
                )
              })
            ) : (
              <tr>
                <td colSpan={8} className='px-4 py-6 text-center text-gray-500'>
                  No modalities found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className='mt-6 flex items-center justify-end'>
          {/* <p className='text-sm text-gray-700'>
            Showing {startIndex}-{endIndex} out of {totalCount || 0} results
          </p> */}

          <div className='flex items-center space-x-2'>
            {/* Previous button */}
            <button
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              className='flex items-center justify-center w-10 h-10 text-gray-500 bg-white border border-black rounded-full hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'>
              <ChevronLeft className='w-4 h-4 text-primary' />
            </button>

            {/* Next button */}
            <button
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className='flex items-center justify-center w-10 h-10 text-gray-500 bg-white border border-black rounded-full hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'>
              <ChevronRight className='w-4 h-4 text-primary' />
            </button>
          </div>
        </div>
      )}

      {/* Modals */}
      <PromptModal open={showPromptModal} onClose={() => setShowPromptModal(false)} sending={sending} onRun={(prompt) => runOneTime(prompt)} />
      <SuccessModal open={showSuccess} onClose={closeSuccessAndRefresh} />
    </PageContainer>
  )
}
